import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useSelector } from "react-redux";
import { NoRowOverlay } from "../../../table/NoRowOverlay";
import * as React from "react";
import LocalizedDataGrid from "../../../ui/datagrid/LocalizedDataGrid";
import Fab from "@mui/material/Fab";
import LinearProgress from "@mui/material/LinearProgress";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { CenterForm } from "./CenterForm";
import {
  getCenters,
  getMonitorCenters,
} from "../../../../redux/actions/center";
import { store } from "../../../../store";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import CSVDownloader from "../../../ui/CSVDownloader";
import { getToday } from "../../../ui/utils/DateUtils";
import CustomFilter from "../../../table/CustomFilter";
import getUniqueOptions from "../../../table/getUniqueOptions";
import { useTranslation } from "react-i18next";
import { useGridApiRef } from "@mui/x-data-grid";

const SPACING = 50;
const createEmptyComboFilters = () => ({
  centerNumber: "",
  country: "",
  city: "",
  status: "",
  contactName: "",
  protocol: "",
});

const getProtocolReferences = (center) =>
  (center?.Protocols || [])
    .map((protocol) => protocol?.reference)
    .filter(Boolean);

const getFilterOptions = (centers, field, translations = {}) =>
  getUniqueOptions(centers, field, translations).filter(
    (option) => option.value !== "",
  );

export const ScreenCenters = () => {
  const apiRef = useGridApiRef();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.user);
  const isMonitor = user.role === "monitor";
  const centerManagerUser =
    user.organization === "Center" && user.role === "manager";
  const adminUser = user.role === "admin";
  const studyCode = location.state?.studyCode;
  const studyId = location.state?.studyId;
  const centers = useSelector((state) => state.center.centers);
  const isLoading = useSelector((state) => state.center.isLoadingCenters);
  const [visibleCreateEdit, setVisibleCreateEdit] = React.useState(false);
  const [selectedCenter, setSelectedCenter] = React.useState(null);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );
  const availableCenters = React.useMemo(
    () => (centerManagerUser ? user.centers || [] : centers || []),
    [centerManagerUser, user.centers, centers],
  );

  const filteredCenters = React.useMemo(() => {
    if (!(isMonitor || centerManagerUser) || !studyId) {
      return availableCenters;
    }

    const selectedStudyId = Number(studyId);
    return availableCenters.filter((center) => {
      const centerStudyId = Number(center?.studyId ?? center?.Study?.id);
      return !Number.isNaN(centerStudyId) && centerStudyId === selectedStudyId;
    });
  }, [availableCenters, centerManagerUser, isMonitor, studyId]);

  const statusTranslations = React.useMemo(
    () => ({
      active: t("active"),
      closed: t("closed"),
      inactive: t("deactivated"),
    }),
    [t],
  );

  const filterOptions = React.useMemo(
    () => ({
      centerNumber: getFilterOptions(filteredCenters, "centerNumber"),
      country: getFilterOptions(filteredCenters, "country"),
      city: getFilterOptions(filteredCenters, "city"),
      status: getFilterOptions(
        filteredCenters,
        "status",
        statusTranslations,
      ),
      contactName: getFilterOptions(filteredCenters, "contactName"),
      protocol: Array.from(
        new Set(filteredCenters.flatMap(getProtocolReferences)),
      )
        .sort((first, second) => first.localeCompare(second))
        .map((reference) => ({ value: reference, label: reference })),
    }),
    [filteredCenters, statusTranslations],
  );

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const activeFilterLabels = React.useMemo(
    () =>
      [
        comboFilters.centerNumber,
        comboFilters.country,
        comboFilters.city,
        statusTranslations[comboFilters.status] || comboFilters.status,
        comboFilters.contactName,
        comboFilters.protocol,
      ].filter(Boolean),
    [comboFilters, statusTranslations],
  );

  const selectedRowsLabel = React.useMemo(() => {
    if (selectedRows.length === 0) {
      return "";
    }

    const count = selectedRows.length.toLocaleString();

    if (i18n.language?.startsWith("fr")) {
      return `${count} ligne${selectedRows.length > 1 ? "s" : ""} sélectionnée${
        selectedRows.length > 1 ? "s" : ""
      }`;
    }

    return `${count} row${selectedRows.length > 1 ? "s" : ""} selected`;
  }, [i18n.language, selectedRows.length]);

  const showFilterStatus =
    activeFilterLabels.length > 0 || selectedRows.length > 0;

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const displayedCenters = React.useMemo(
    () =>
      filteredCenters.filter((center) => {
        const matchesValue = (field) =>
          !comboFilters[field] ||
          String(center?.[field]) === String(comboFilters[field]);

        return (
          matchesValue("centerNumber") &&
          matchesValue("country") &&
          matchesValue("city") &&
          matchesValue("status") &&
          matchesValue("contactName") &&
          (!comboFilters.protocol ||
            getProtocolReferences(center).includes(comboFilters.protocol))
        );
      }),
    [comboFilters, filteredCenters],
  );

  const selectedCentersForDownload = React.useMemo(
    () =>
      filteredCenters
        .filter((center) => selectedRows.includes(center.id))
        .map((center) => ({
          [t("centerNumber")]: center.centerNumber,
          [t("country")]: center.country,
          [t("city")]: center.city,
          [t("status")]: center.status,
          [t("contact")]: center.contactName,
          [t("protocol")]: getProtocolReferences(center).join(", "),
        })),
    [filteredCenters, selectedRows, t],
  );

  React.useEffect(() => {
    const visibleIds = new Set(displayedCenters.map((center) => center.id));
    setSelectedRows((currentRows) =>
      currentRows.filter((rowId) => visibleIds.has(rowId)),
    );
  }, [displayedCenters]);

  // ─── Cell Click Handler ───────────────────────────────────────────────────
  const handleCellClick = (params) => {
    if (!["__check__", "edit"].includes(params.field)) {
      navigate(`/productions`, {
        state: {
          centerId: params.row.id,
          centerNumber: params.row.centerNumber,
          studyId: studyId,
          studyCode: studyCode,
        },
      });
    }
  };

  const handleEdit = React.useCallback((center) => {
    setSelectedCenter(center);
    setVisibleCreateEdit(true);
  }, []);

  const columns = React.useMemo(() => {
    const comboFilterOperator = (field, label, options, matches) => [
      {
        label,
        value: field,
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) {
            return null;
          }

          return matches
            ? (params) => matches(params.row, filterItem.value)
            : ({ value }) => String(value) === String(filterItem.value);
        },
        InputComponent: (filterProps) => (
          <CustomFilter
            {...filterProps}
            label={label}
            options={options}
            item={{
              ...filterProps.item,
              value: filterProps.item.value || comboFilters[field],
            }}
            applyValue={(nextItem) => {
              filterProps.applyValue(nextItem);
              updateComboFilter(field, nextItem.value);
            }}
          />
        ),
      },
    ];

    return [
      ...(adminUser
        ? [
            {
              field: "edit",
              headerName: "",
              width: 50,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params) => (
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={t("edit")}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEdit(params.row);
                  }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              ),
            },
          ]
        : []),
      {
        field: "centerNumber",
        headerName: t("centerNumber"),
        flex: 0.2,
        filterable: true,
        filterOperators: comboFilterOperator(
          "centerNumber",
          t("centerNumber"),
          filterOptions.centerNumber,
        ),
        renderCell: (params) => (
          <div
            style={{ cursor: "pointer" }}
            // onClick={() => handleCellClick(params)}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: "country",
        headerName: t("country"),
        flex: 0.2,
        renderCell: (params) => String(params.value || "").toUpperCase(),
        filterable: true,
        filterOperators: comboFilterOperator(
          "country",
          t("country"),
          filterOptions.country,
        ),
      },
      {
        field: "city",
        headerName: t("city"),
        flex: 0.2,
        filterable: true,
        filterOperators: comboFilterOperator(
          "city",
          t("city"),
          filterOptions.city,
        ),
        renderCell: (params) =>
          String(params.value || "")
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" "),
      },
      {
        field: "status",
        headerName: t("status"),
        flex: 0.1,
        filterable: true,
        filterOperators: comboFilterOperator(
          "status",
          t("status"),
          filterOptions.status,
        ),
        renderCell: (params) =>
          statusTranslations[params.value] || params.value,
      },
      {
        field: "contactName",
        headerName: t("contact"),
        flex: 0.3,
        filterable: true,
        filterOperators: comboFilterOperator(
          "contactName",
          t("contact"),
          filterOptions.contactName,
        ),
        renderCell: (params) => {
          if (!params.value || !params.value.includes(",")) {
            return params.value;
          }
          const parts = params.value.split(",");
          const lastName = parts[0].trim();
          const firstName = parts.length > 1 ? parts[1].trim() : "";
          const formattedFirstName = firstName
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
          return `${lastName}, ${formattedFirstName}`;
        },
      },
      {
        field: "Protocols",
        headerName: t("protocol"),
        flex: 0.2,
        filterable: true,
        filterOperators: comboFilterOperator(
          "protocol",
          t("protocol"),
          filterOptions.protocol,
          (center, reference) =>
            getProtocolReferences(center).includes(reference),
        ),
        renderCell: (params) => {
          return getProtocolReferences(params.row).join(", ");
        },
      },
    ];
  }, [
    adminUser,
    comboFilters,
    filterOptions,
    handleEdit,
    statusTranslations,
    t,
    updateComboFilter,
  ]);

  React.useEffect(() => {
    if (isMonitor) {
      store.dispatch(getMonitorCenters(user.id));
    } else if (centerManagerUser) {
      if (!studyId) {
        navigate("/studies");
      }
    } else if (studyId) {
      store.dispatch(getCenters(studyId));
    } else {
      navigate("/studies");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerManagerUser, isMonitor, studyId, user.id]);

  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
        }}>
        <div
          style={{ width: "100%", height: "85vh", position: "relative" }}>
          <LocalizedDataGrid
            apiRef={apiRef}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: NoRowOverlay,
              loadingOverlay: LinearProgress,
            }}
            onCellClick={handleCellClick}
            rows={displayedCenters}
            columns={columns}
            loading={centerManagerUser ? false : isLoading}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 20 },
              },
            }}
            pageSizeOptions={[20, 50]}
            checkboxSelection
            hideFooterSelectedRowCount
            rowHeight={40}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
          />
          {showFilterStatus && (
            <Box
              sx={{
                position: "absolute",
                left: 16,
                bottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 1,
                maxWidth: "70%",
                color: "text.secondary",
                fontSize: "0.875rem",
                zIndex: 2,
              }}>
              {activeFilterLabels.length > 0 && (
                <IconButton
                  aria-label="Reset filters"
                  onClick={handleResetFilters}
                  size="small"
                  sx={{ p: 0.25, flexShrink: 0 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                {activeFilterLabels.length > 0
                  ? `Filters: ${activeFilterLabels.join(", ")}`
                  : selectedRowsLabel}
              </Box>
              {activeFilterLabels.length > 0 && selectedRowsLabel && (
                <Box
                  component="span"
                  sx={{ color: "text.primary", flexShrink: 0 }}>
                  {selectedRowsLabel}
                </Box>
              )}
            </Box>
          )}
        </div>
      </Paper>
      {selectedCentersForDownload.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
          <CSVDownloader
            data={selectedCentersForDownload}
            filename={`${studyCode}_centers_${getToday()}`}
            name={t("selectedCenters")}
          />
        </Box>
      )}
      {adminUser && (
        <>
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => {
              setSelectedCenter(null);
              setVisibleCreateEdit(true);
            }}
            style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
            <AddIcon />
          </Fab>
          <CenterForm
            visible={visibleCreateEdit}
            studyId={studyId}
            center={selectedCenter}
            onClose={(refresh) => {
              setVisibleCreateEdit(false);
              setSelectedCenter(null);
              if (refresh) {
                store.dispatch(getCenters(studyId));
              }
            }}></CenterForm>
        </>
      )}
    </Grid>
  );
};
